import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";

import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import ProductCard from "@/components/product/ProductCard";

import { Product } from "@/services/types/domain";
import wishlistApi from "@/services/api/wishlist"; // el servicio que creamos
import { useAuth } from "@/store/authStore";

// Tipo que refleja EXACTAMENTE lo que devuelve tu backend en /wishlist
export type BackendWishlistRow = {
    wishlist_id: number;
    user_id: number;
    product_id: number;
    added_at: string;
    product_name: string;
    product_slug: string;
    price: number;
    compare_price: number | null;
    product_status: string; // "active" | ...
    stock_quantity: number;
    rating_average: number;
    rating_count: number;
    category_name: string;
    brand_name: string | null;
    store_name: string;
    seller_rating: string; // ej. "5.00"
    primary_image: string | null; // ruta relativa o absoluta
    discount_percentage: number; // 0..100
    is_on_sale: boolean;
    is_available: boolean;
    is_in_stock: boolean;
};

// Mapea el row del backend al mínimo necesario por ProductCard
const mapRowToProduct = (row: BackendWishlistRow): Product => {
    const category: any = { id: 0, name: row.category_name || "General" };

    const p: any = {
        id: row.product_id,
        name: row.product_name,
        slug: row.product_slug,
        description: row.product_name,
        sku: `SKU-${row.product_id}`,
        price: Number(row.price),
        compare_price: row.compare_price ?? null,
        stock_quantity: row.stock_quantity ?? 0,
        // rating usados por la card
        average_rating: row.rating_average || 0,
        reviews_count: row.rating_count || 0,
        // imagen principal (la card usa fallback si images está vacío)
        primary_image: row.primary_image || undefined,
        images: [],
        category,
        has_variants: false,
        // banderas útiles
        is_active: row.product_status === "active",
        // campos de relleno para cumplir tipos estrictos (no usados en la card)
        created_at: "",
        updated_at: "",
    };
    return p as Product;
};

const WishlistPage: React.FC = () => {
    const { isAuthenticated } = useAuth();

    const [items, setItems] = useState<BackendWishlistRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // paginación del backend adaptada
    const [page, setPage] = useState(1);
    const [perPage] = useState(24);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    const fetchList = async (p = 1) => {
        try {
            setLoading(true);
            setError(null);
            const res = await wishlistApi.list({ page: p, per_page: perPage });
            // res.data puede ser Product[] o BackendWishlistRow[] según el adaptador genérico.
            // Aquí forzamos a BackendWishlistRow mediante una verificación ligera.
            const rows = (res.data as any[]).map((x) => {
                // si ya viene con product (otro formato), normalizamos
                if ((x as any).product_id) return x as BackendWishlistRow;
                // si viniera tipo Product, habría que construir un row o mapear directo a ProductCard
                return x as BackendWishlistRow;
            });
            setItems(rows);
            setTotal(res.meta.total);
            setLastPage(res.meta.last_page);
            setPage(res.meta.current_page);
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "No se pudo cargar tu lista de deseos";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!isAuthenticated) { setLoading(false); return; }
            await fetchList(1);
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
    }, [isAuthenticated]);

    const remove = async (row: BackendWishlistRow) => {
        try {
            await wishlistApi.remove(row.product_id);
            setItems(prev => prev.filter(x => x.product_id !== row.product_id));
            setTotal(prev => Math.max(0, prev - 1));
            toast.success("Eliminado de tu lista");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo eliminar");
        }
    };

    const goToPage = async (p: number) => {
        if (p < 1 || p > lastPage) return;
        await fetchList(p);
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50 px-4 py-12">
                <div className="max-w-md w-full rounded-2xl border bg-white p-8 text-center shadow-sm">
                    <Heart className="mx-auto mb-3 w-8 h-8 text-primary-600" />
                    <div className="mb-1 text-2xl font-bold text-gray-900">Tu lista de deseos</div>
                    <p className="mb-6 text-gray-600">Inicia sesión para ver y guardar tus productos favoritos.</p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                        <Button asChild><Link to="/login">Iniciar sesión</Link></Button>
                        <Button variant="secondary" asChild><Link to="/register">Crear cuenta</Link></Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-7xl">
                <div className="mb-4 flex items-center justify-between">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Mi lista de deseos</h1>
                    <Badge variant="outline">{total} items</Badge>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="h-64 rounded-2xl bg-white border animate-pulse"
                                initial={{ opacity: 0.8 }}
                                animate={{ opacity: 1 }}
                            />
                        ))}
                    </div>
                ) : error ? (
                    <div className="rounded-xl border bg-white p-6 text-center text-red-600">{error}</div>
                ) : items.length === 0 ? (
                    <div className="rounded-xl border bg-white p-8 text-center">
                        <div className="mb-2 text-lg font-semibold">Aún no tienes favoritos</div>
                        <p className="text-gray-600">Explora y agrega productos a tu lista de deseos.</p>
                        <div className="mt-4">
                            <Button asChild><Link to="/">Ver productos</Link></Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {items.map((row) => {
                                const product = mapRowToProduct(row);
                                return (
                                    <motion.div
                                        key={`${row.wishlist_id}-${row.product_id}`}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="relative"
                                    >
                                        <ProductCard product={product} showWishlist={false} />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="absolute top-2 left-2 rounded-full !p-2"
                                            onClick={() => remove(row)}
                                            leftIcon={<Trash2 className="w-4 h-4" />}
                                        >
                                            Quitar
                                        </Button>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Paginación simple */}
                        <div className="mt-6 flex items-center justify-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                                <ArrowLeft className="w-4 h-4" />
                                <span className="ml-1">Anterior</span>
                            </Button>
                            <span className="text-sm text-gray-700">
                                Página <strong>{page}</strong> de <strong>{lastPage}</strong>
                            </span>
                            <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)} disabled={page >= lastPage}>
                                <span className="mr-1">Siguiente</span>
                                <ArrowRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WishlistPage;
